import { HttpClient } from "@angular/common/http";
import { Injectable, inject, signal } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Observable, delay, map, of, share, tap } from "rxjs";
import { environment } from "../../environments/environment";
import {
  DecodedToken,
  DecodedTokenNormalized,
  JwtTokenResponse,
} from "../interfaces/jwt-token.interface";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly sellPolicyModlue = "53239909-62be-4ff3-8826-13e7d6a0c0d1";
  private readonly santandeCnpPackages = ["332", "333"];
  private readonly tokenKey = "glob-api-user-token";
  private readonly tokenApiUrl = environment.tokenApiUrl;
  private readonly httpClient = inject(HttpClient);
  private readonly jwtHelper = inject(JwtHelperService);

  getTokenRequest$: Observable<string | undefined> = this.getToken().pipe(
    share(),
    map((token) => {
      if (token.access_token)
        sessionStorage.setItem(this.tokenKey, token.access_token);
      return token.access_token;
    }),
  );
  currentDecodedToken = signal<DecodedTokenNormalized | undefined>(undefined);

  getRawToken(): Observable<string | undefined> {
    const tokenLocal = sessionStorage.getItem(this.tokenKey);

    if (tokenLocal === null || this.jwtHelper.isTokenExpired(tokenLocal, 30)) {
      return this.getTokenRequest$;
    } else {
      return of(tokenLocal);
    }
  }

  getRawTokenFromApi(): Observable<string | undefined> {
    return this.getToken().pipe(
      map((token) => {
        if (token.access_token)
          sessionStorage.setItem(this.tokenKey, token.access_token);
        return token.access_token;
      }),
    );
  }

  getDecodedToken(): Observable<DecodedTokenNormalized | undefined> {
    return this.getRawToken().pipe(
      map((token) =>
        token ? (this.jwtHelper.decodeToken(token) as DecodedToken) : undefined,
      ),
      map(
        (decodedToken) =>
          ({
            ...decodedToken,
            module: decodedToken?.module
              ? this.takeArray(decodedToken?.module)
              : undefined,
            insurance_package: decodedToken?.insurance_package
              ? this.takeArray(decodedToken?.insurance_package)
              : undefined,
          }) as DecodedTokenNormalized,
      ),
      tap((decoded) => this.currentDecodedToken.set(decoded)),
    );
  }

  isTokenInStorage() {
    return !!sessionStorage.getItem(this.tokenKey);
  }

  isSantanderCnpAuthorized(): Observable<boolean> {
    return this.getDecodedToken().pipe(
      map(
        (tokenData) =>
          !!tokenData &&
          !!tokenData?.module &&
          !!tokenData?.insurance_package &&
          tokenData?.module.includes(this.sellPolicyModlue) &&
          tokenData?.insurance_package.some((pkg) =>
            this.santandeCnpPackages.includes(pkg),
          ),
      ),
    );
  }

  redirectToLoginSso() {
    sessionStorage.clear();
    window.location.replace(
      `${environment.ssoUrl}/LogIn?ReturnUrl=${encodeURIComponent(
        window.location.pathname.toString(),
      )}`,
    );
  }

  redirectToAccessDeniedSso() {
    sessionStorage.clear();
    window.location.replace(
      `${environment.ssoUrl}/Account/Denied?ReturnUrl=${encodeURIComponent(
        window.location.pathname.toString(),
      )}`,
    );
  }

  logout() {
    sessionStorage.clear();
    window.location.replace(`${environment.ssoUrl}/account/logout`);
  }

  private getToken(): Observable<JwtTokenResponse> {
    if (!environment.production && !!environment.devToken) {
      return of<JwtTokenResponse>({
        access_token: environment.devToken,
        token_type: "bearer",
      }).pipe(delay(1000));
    }

    return this.httpClient.get<JwtTokenResponse>(
      `${this.tokenApiUrl}/access/token`,
      {
        params: {
          grant_type: "implicit",
          language: "pl",
          locale: "pl",
        },
      },
    );
  }

  private takeArray(value: string | string[]): string[] {
    if (Array.isArray(value)) return value;
    return [value];
  }
}
